/**
 * A drop's groups: one chip per group that sends at its time. A click on the
 * chips (or on +) opens the picker — tick or untick a group there, so a group
 * never leaves a drop by a stray click (user, 11/10/2026: a one-click "take
 * off" chip emptied a drop). Two or more send together, each to its own list,
 * and then each may carry its own amount. No group = never planned.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Plus } from 'lucide-react';

import type { DraftSlot } from '../../../../../drop-generator.types';
import { DropGroupOption } from './DropGroupOption';
import { GroupChip } from '../../../../shared/GroupChip';
import type { GroupRef } from '../../hooks/use-general-times';
import { dropKey } from '../../../state/slots/group-keyed';
import type { SlotPatch } from '../../../state/slots/slot-ops';

interface Props {
  slot: DraftSlot;
  position: number;
  groupRefs: GroupRef[];
  defaultAmount: number;
  /** Show only this group's chip (one line of the row's list cell); the picker still edits every group. */
  only?: GroupRef;
  onPatch: (patch: SlotPatch) => void;
}

export const DropGroups: FC<Props> = ({ slot, position, groupRefs, defaultAmount, only, onPatch }) => {
  const listed = only ? [only] : groupRefs.filter((g) => slot.listGroupIds.includes(g.id));
  const toggle = (id: string) => {
    const on = slot.listGroupIds.includes(id);
    const listGroupIds = on
      ? slot.listGroupIds.filter((g) => g !== id)
      : groupRefs.map((g) => g.id).filter((g) => g === id || slot.listGroupIds.includes(g));
    const pins = { ...slot.listsByGroup };
    if (on) delete pins[id];
    const own = { ...(slot.amountsByGroup ?? {}) };
    if (on) delete own[id];
    onPatch({
      listsByGroup: Object.keys(pins).length ? pins : null,
      listsRankByGroup: on ? dropKey(slot.listsRankByGroup, id) : (slot.listsRankByGroup ?? null),
      listGroupIds,
      amountsByGroup: Object.keys(own).length > 0 ? own : null,
    });
  };
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={only ? `Drop ${position}: ${only.name} — pick its groups` : `Drop ${position}: pick its groups`}
          title="Pick the groups of this drop"
          className="group/groups flex max-w-full min-w-0 flex-col items-start gap-1 rounded-full text-left transition-colors hover:opacity-80"
        >
          {listed.map((g) => (
            <span key={g.id} className="flex max-w-full min-w-0 items-center gap-1">
              <span className="min-w-0 [&>span]:max-w-full">
                <GroupChip name={g.name} color={g.color} />
              </span>
              {slot.amountsByGroup?.[g.id] !== undefined ? (
                <span className="font-mono text-[11px] tabular-nums text-primary" title={`${g.name}'s own amount on this drop`}>
                  ×{slot.amountsByGroup[g.id].toLocaleString('en-US')}
                </span>
              ) : null}
            </span>
          ))}
          {listed.length === 0 ? (
            <span className="inline-flex min-h-7 items-center gap-1 rounded-md border border-dashed border-input px-2 text-[11px] text-muted-foreground group-hover/groups:border-primary group-hover/groups:text-primary">
              <Plus className="h-3 w-3" />
              Choose group
            </span>
          ) : null}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          align="start"
          className="z-50 flex w-[min(420px,calc(100vw-24px))] flex-col gap-1 rounded-lg border bg-popover p-3 text-sm shadow-lg animate-in fade-in-0 zoom-in-95"
        >
          <span className="mb-1 text-xs font-semibold">List groups for this drop</span>
          {groupRefs.length === 0 ? <p className="text-xs text-muted-foreground">Link a list group first (List groups tab).</p> : null}
          {groupRefs.map((g) => (
            <DropGroupOption
              key={g.id}
              group={g}
              slot={slot}
              defaultAmount={defaultAmount}
              together={slot.listGroupIds.length > 1}
              onToggle={() => toggle(g.id)}
              onPatch={onPatch}
            />
          ))}
          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
            Two or more send together, each to its own list. A group set to all lists (List groups tab) sends one drop to each of its best
            lists.
          </p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
